import React, {
  forwardRef,
  PropsWithChildren,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  ReactNode,
  Fragment,
  useCallback,
} from "react";

import Skeleton from "@material-ui/lab/Skeleton";
import Paper from "@material-ui/core/Paper";

import FullCalendar, {
  EventClickArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import itLocale from "@fullcalendar/core/locales/it";

import { useSnackbarQuery } from "../../../hooks";

import useGqlCalendarStyles from "./gql_calendar.style";
import { JsxElement } from "typescript";

export type GqlCalendarEventBuilder = (value: any) => EventInput;

export interface GqlCalendarProps extends PropsWithChildren<{}> {
  query: any;
  queryVariables?: any;
  queryResultDataField: string;
  eventBuilder: GqlCalendarEventBuilder;
  eventRenderer?: (evt: EventContentArg) => JsxElement;
  eventClick: (arg: EventClickArg, queryDataElement: any) => void;
  visibleFrom: Date;
  visibleTo: Date;
  autoRefreshTimeoutInSeconds?: number;
  dayHeaderContent?: (date: Date) => ReactNode | JSX.Element;
}

const GqlCalendar = forwardRef(
  (props: GqlCalendarProps, ref: Ref<typeof GqlCalendar>) => {
    const {
      query,
      queryVariables,
      queryResultDataField,
      autoRefreshTimeoutInSeconds = 60,
      visibleFrom,
      visibleTo,
      eventBuilder,
      eventRenderer,
      eventClick,
      dayHeaderContent,
    } = props;

    const classes = useGqlCalendarStyles();

    const { loading, data, error, refetch, startPolling, stopPolling } =
      useSnackbarQuery(query, {
        variables: queryVariables,
      });

    useEffect(() => {
      if (startPolling && autoRefreshTimeoutInSeconds) {
        startPolling(autoRefreshTimeoutInSeconds * 1000);
      }
    }, [startPolling, autoRefreshTimeoutInSeconds]);

    useEffect(() => {
      // Destructor
      return () => {
        stopPolling();
      };
    }, [stopPolling]);

    const queryData = useMemo(() => {
      if (data && queryResultDataField) {
        return data[queryResultDataField];
      }
      return [];
    }, [data, queryResultDataField]);

    const fullCalendarEvents = useMemo(() => {
      if (queryData) {
        return queryData.map((v) => eventBuilder(v));
      }
      return [];
    }, [queryData, eventBuilder]);

    useImperativeHandle(ref, () => ({
      refresh: () => {
        refetch();
      },
    }));

    const onEventClick = (evt: EventClickArg) => {
      const queryDataElementRef = queryData.find((el) => {
        return el.id === evt.event.id;
      });

      eventClick(evt, queryDataElementRef);
    };

    const defaultRenderer = useCallback((evt: EventContentArg) => {
      const rows = evt.event.title.split("|");
      return (
        <>
          <span>{rows[0]}</span>
          {rows.map(
            (entry: string, index) =>
              index > 0 && (
                <>
                  <br />
                  <span>{entry}</span>
                </>
              )
          )}
        </>
      );
    }, []);

    const effectiveRenderer = useCallback(
      (evt: EventContentArg) => {
        if (eventRenderer) return eventRenderer(evt)!;
        else return defaultRenderer(evt);
      },
      [eventRenderer, defaultRenderer]
    );

    if (loading) {
      return <Skeleton animation="wave" />;
    }
    if (error) {
      return <span>-</span>;
    }
    return (
      <Paper elevation={3} className={classes.root}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin]}
          initialView="timeGridWeek"
          weekends={true}
          locale={itLocale}
          events={fullCalendarEvents}
          eventContent={effectiveRenderer}
          eventClick={onEventClick}
          validRange={{
            start: visibleFrom,
            end: visibleTo,
          }}
          dayHeaderContent={(arg) => {
            const { date }: { date: Date } = arg;
            // console.log('-> dayHeaderContent is', dayHeaderContent);
            return (
              <Fragment>
                <span>
                  {date.toLocaleString("it", {
                    day: "numeric",
                    month: "numeric",
                    weekday: "short",
                  })}
                </span>
                <br />
                {dayHeaderContent && dayHeaderContent(date)}
              </Fragment>
            );
          }}
          scrollTime={new Date().toLocaleTimeString()}
          height={"100%"}
        />
      </Paper>
    );
  }
);

export { GqlCalendar };
